import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { buildPhaseService } from '../../services/buildPhaseService';
import { type BuildFlowResolvedDetail, isSuccessfulBuildResolution } from '../ChatBox/buildFlowResolution';
import { dispatchStartBuild, useBuildResolvedListener } from '../ChatBox/StartBuildingButton';

import {
  buildPressPrecheck, type BuildPressCheck, prerequisitesRefusal, SELECT_TASKS_REFUSAL, TASK_PLAN_MISSING_REFUSAL,
} from './buildPressRefusal';
import type { PressRefusal } from './pressRefusal';
import { type PressFeedback, usePressFeedback } from './usePressFeedback';

/** A press with no answer this long (no build listener mounted, a hung request) says so instead of spinning. */
export const BUILD_ANSWER_WATCHDOG_MS = 45_000;

export const NO_ANSWER_REFUSAL: PressRefusal = {
  code: 'no_answer',
  message: 'The build did not answer, so nothing was started.',
  fix: { kind: 'retry', label: 'Try Again' },
};

export interface BuildPressRequest extends BuildPressCheck {
  projectId?: string | null;
  phaseId: string | null;
  selectedTaskIds?: string[];
  selectionSummaryId?: string;
}

export interface BuildPressHandlers {
  /** Post the press's chat receipt; fire and forget, a press never waits for it. */
  record: () => void;
  /** Bring the phase task picker back after a press with nothing selected. */
  restoreSelector: () => void;
  /** The build started: mark the gate answered. */
  onStarted?: () => void;
  /** Re-read the phase state: once the batch exists the label reads "Building Phase N…" from it. */
  refresh: () => Promise<void>;
}

export interface BuildPress {
  feedback: PressFeedback;
  /** Queued or running: the button is disabled and spins (aria-busy, so the sticky dock spins too). */
  busy: boolean;
  press: () => void;
}

type ClickIdRef = MutableRefObject<string | null>;

const noop = (): void => {};

/** Show the pressed button's refusal in place and say so on the event, so the build flow does not toast it too. */
const claimRefusal = (
  detail: BuildFlowResolvedDetail, refuse: (refusal: PressRefusal) => void, reset: () => void,
): void => {
  if (!detail.refusal) return reset();
  detail.refusalShown = true;

  return refuse(detail.refusal);
};

/** A press refused for want of a task plan lists every earlier step still open, read from the server. */
export const explainPrerequisites = async (
  projectId: string | null | undefined, refusal: PressRefusal,
): Promise<PressRefusal> => {
  if (!projectId || refusal.code !== TASK_PLAN_MISSING_REFUSAL.code) return refusal;
  const readiness = await buildPhaseService.getBuildReadiness(projectId).catch(() => null);

  return prerequisitesRefusal(refusal, readiness?.prerequisites ?? []);
};

/** Refuse at once, or after reading what must happen first; the press stays queued (spinning) meanwhile. */
const useExplainedRefuse = (
  projectId: string | null | undefined, refuse: (refusal: PressRefusal) => void,
): ((refusal: PressRefusal) => void) => useCallback((refusal: PressRefusal) => {
  if (refusal.code !== TASK_PLAN_MISSING_REFUSAL.code) return refuse(refusal);
  void explainPrerequisites(projectId, refusal).then(refuse);
}, [projectId, refuse]);

/** Clear a press that never heard back, so it says so instead of spinning forever. */
const useBuildWatchdog = (queued: boolean, clickIdRef: ClickIdRef, refuse: (refusal: PressRefusal) => void): void => {
  useEffect(() => {
    if (!queued) return undefined;
    const timer = window.setTimeout(() => {
      if (!clickIdRef.current) return;
      clickIdRef.current = null;
      refuse(NO_ANSWER_REFUSAL);
    }, BUILD_ANSWER_WATCHDOG_MS);

    return () => window.clearTimeout(timer);
  }, [clickIdRef, queued, refuse]);
};

/** The pressed button's resolution: running after a start, else the refusal the server gave. */
const useBuildResolution = (
  feedback: PressFeedback, clickIdRef: ClickIdRef, { onStarted, refresh }: BuildPressHandlers,
  projectId?: string | null,
): void => {
  const { accept, reset } = feedback;
  const refuse = useExplainedRefuse(projectId, feedback.refuse);
  const started = useCallback(() => {
    accept();
    onStarted?.();
    void refresh().finally(reset);
  }, [accept, onStarted, refresh, reset]);
  const settle = useCallback((detail: BuildFlowResolvedDetail) => {
    const clickId = clickIdRef.current;

    clickIdRef.current = null;
    if (!clickId) return;
    if (isSuccessfulBuildResolution(detail, clickId)) return started();

    return claimRefusal(detail, refuse, reset);
  }, [clickIdRef, refuse, reset, started]);

  useBuildResolvedListener(clickIdRef, noop, settle);
  useBuildWatchdog(feedback.phase === 'queued', clickIdRef, feedback.refuse);
};

const useBuildPressHandler = (
  request: BuildPressRequest, handlers: BuildPressHandlers, feedback: PressFeedback, clickIdRef: ClickIdRef,
): (() => void) => {
  const { begin } = feedback;
  const { record, restoreSelector } = handlers;
  const { state, selectionEmpty, creditShortfall, phaseId, selectedTaskIds, selectionSummaryId } = request;
  const refuse = useExplainedRefuse(request.projectId, feedback.refuse);
  const refuseAtOnce = useCallback((refusal: PressRefusal) => {
    if (refusal.code === SELECT_TASKS_REFUSAL.code) restoreSelector();
    refuse(refusal);
  }, [refuse, restoreSelector]);

  return useCallback(() => {
    if (!begin()) return;
    const refusal = buildPressPrecheck({ state, selectionEmpty, creditShortfall });

    if (refusal) return refuseAtOnce(refusal);
    record();
    dispatchStartBuild(clickIdRef, phaseId, selectedTaskIds, selectionSummaryId);
  }, [begin, clickIdRef, creditShortfall, phaseId, record, refuseAtOnce, selectedTaskIds, selectionEmpty,
    selectionSummaryId, state]);
};

/**
 * One build button's press: queued at once (spinner, disabled, a second press ignored); a refusal
 * the bar answers without a request (empty selection, known shortfall, a phase that cannot start);
 * else the start request, then running until the phase state shows the batch, or the server's
 * reason with its fix. Only the pressed button reacts: resolutions are matched by click id.
 */
export const useBuildPress = (request: BuildPressRequest, handlers: BuildPressHandlers): BuildPress => {
  const feedback = usePressFeedback();
  const clickIdRef = useRef<string | null>(null);

  useBuildResolution(feedback, clickIdRef, handlers, request.projectId);
  const press = useBuildPressHandler(request, handlers, feedback, clickIdRef);

  return { feedback, busy: feedback.phase === 'queued' || feedback.phase === 'running', press };
};
